import { useTrackPaywallViewedOncePerOpen } from '../../../trials-and-upgrades/hooks/useTrackPaywallViewedOncePerOpen';
import { pricingPlansModalOpenAtom } from '../billingAtoms';
import { useSubscriptionUpgrade } from './useSubscriptionUpgrade';

import { useNinetyDayTrialVariant } from '../../../trials-and-upgrades/ninety-day-end-of-trial/useNinetyDayTrialVariant';
import { trialRecapModalMessages } from '../../../trials-and-upgrades/trial-recap-modal/messages';
import { getPlanChangesHelpArticleUrl } from '../../../trials-and-upgrades/trial-recap-modal/utils';

import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { getTeamContextAnalyticsProps } from '../../../trials-and-upgrades/utils/getTeamContextAnalyticsProps';

import { type Period } from '@heidi/commerce/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { SubscriptionStatus, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  useUserBillingInfo as useQueryUserBillingInfo,
  useUserTier as useQueryUserTier,
  useReverseTrial,
} from '../../../../data/injected-billing';
import { getCommerceBillingUiPorts } from '../billing-ui-ports';

export interface UsePracticeUpgradeModalProps {
  launchOpen?: boolean;
  onLaunchClose?: () => void;
}

export function usePracticeUpgradeModal({
  launchOpen,
  onLaunchClose,
}: UsePracticeUpgradeModalProps = {}) {
  const [isAtomOpen, setIsAtomOpen] =
    getCommerceBillingUiPorts().usePracticeUpgradeModalState();
  const [isFromTrialRecap, setIsFromTrialRecap] =
    getCommerceBillingUiPorts().useUpgradeModalFromTrialRecapState();
  const previewDaysLeft = getCommerceBillingUiPorts().useTrialModalPreviewDaysLeftValue();
  const setPreviewDaysLeft =
    getCommerceBillingUiPorts().useSetTrialModalPreviewDaysLeft();
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const setTrialExtensionModalOpen =
    getCommerceBillingUiPorts().useSetTrialExtensionModalOpen();
  const { t } = useTranslations();
  const intl = useIntl();

  const isOpen = isAtomOpen || (launchOpen ?? false);

  const {
    clinicianTrialEnded,
    clinicianTrialDaysLeft,
    isClinicianPracticeTrialUser,
    previousStatusBeforeClinicianTrial,
    isInReverseTrial,
    isFakeClinicianTrial,
  } = useReverseTrial();
  const { userTier } = useQueryUserTier();
  const {
    hasTeam,
    isTeamAdmin,
    isTeamStripeManaged,
    isTeamDataReady,
    isJoinedTeamsFetched,
  } = useCombinedQueriesTeamGeneralInfo();
  const effectiveDaysLeft =
    previewDaysLeft !== null ? previewDaysLeft : clinicianTrialDaysLeft;
  const isPreview = previewDaysLeft !== null;

  const wasPreviouslyPracticeOld =
    previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO_PLUS ||
    userTier === SubscriptionTier.PRO_PLUS;

  const handleClose = useCallback(() => {
    setIsAtomOpen(false);
    setPreviewDaysLeft(null);
    setIsFromTrialRecap(false);
    onLaunchClose?.();
  }, [setIsAtomOpen, setPreviewDaysLeft, setIsFromTrialRecap, onLaunchClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      } else {
        setIsAtomOpen(true);
      }
    },
    [handleClose, setIsAtomOpen]
  );

  const handleViewAllPlans = useCallback(() => {
    handleClose();
    setPricingPlansModalOpen(true);
  }, [handleClose, setPricingPlansModalOpen]);

  const handleExtendTrial = useCallback(() => {
    handleClose();
    setTrialExtensionModalOpen(true);
  }, [handleClose, setTrialExtensionModalOpen]);

  const { data: products, catalogueVersion } =
    getCommerceBillingUiPorts().products.useQueryProducts({
      heidi2Catalogue: true,
    });
  const { data: billingInfo } = useQueryUserBillingInfo();
  const newProductTier = billingInfo?.new_product_tier;
  const practiceProduct = getCommerceBillingUiPorts().products.getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO_PLUS,
    products
  );

  const { practiceFeatures, practiceShortFeatures, practiceNewFeatures } =
    getCommerceBillingUiPorts().useFeatureLists();

  const {
    showNinetyDayTrialVariant: rawShowNinetyDayTrialVariant,
    isNinetyDayTrialEnded,
    isUpgradeDisabledForTeamMember,
    shouldDisableButtonForNonStripeManagedTeam,
    showNonAdminRequestBanner: rawShowNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade: rawShowContactSalesInsteadOfUpgrade,
    planDisplayName: practicePlanDisplayName,
    handleContactSalesClick,
    hasCallbackRequested,
  } = useNinetyDayTrialVariant({
    isOpen,
    trackContactSalesClick:
      getCommerceBillingUiPorts().trackPracticeUpgradeModalContactSalesClick,
    planNameMessage: trialRecapModalMessages.practicePlanName,
    onClose: handleClose,
  });

  useTrackPaywallViewedOncePerOpen({
    isOpen,
    isTeamsFetched: isJoinedTeamsFetched,
    isTeamDataReady,
    hasTeam,
    onTrack: () => {
      getCommerceBillingUiPorts().track({
        options: { amplitude: false },
        event_name: getCommerceBillingUiPorts().trackingEventName.PaywallViewed,
        props: {
          source: getCommerceBillingUiPorts().paywallSource.PracticeUpgradeModal,
          target_plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
          days_left_on_trial: clinicianTrialDaysLeft,
          is_90_day_trial_user: isFakeClinicianTrial,
          is_90_day_trial_ended: isNinetyDayTrialEnded,
          ...getTeamContextAnalyticsProps({
            hasTeam,
            isTeamAdmin,
            isTeamStripeManaged,
          }),
        },
      });
    },
  });

  // When opened from the post-90-day trial-ended recap modal we re-use the
  // 90-day variant layout (grouped pricing card, UpgradeTrialFeatureList,
  // UpgradeTermsFooter, "Keep Practice" CTA, Talk-to-us secondary) even
  // though the trial has technically ended. The modal component additionally
  // swaps the title and hides the testimonial right panel for this variant.
  const showNinetyDayTrialVariant = rawShowNinetyDayTrialVariant || isFromTrialRecap;
  const showNonAdminRequestBanner =
    rawShowNonAdminRequestBanner || (isFromTrialRecap && isUpgradeDisabledForTeamMember);
  const showContactSalesInsteadOfUpgrade =
    rawShowContactSalesInsteadOfUpgrade ||
    (isFromTrialRecap && shouldDisableButtonForNonStripeManagedTeam);

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });
  const handleUpgradeClick = useCallback(
    (period: Period) => {
      getCommerceBillingUiPorts().track({
        event_name: getCommerceBillingUiPorts().trackingEventName.ButtonClicked,
        props: {
          screen_name: ScreenName.UpgradeToPracticeModal,
          action: ButtonAction.PracticeUpgradeModalUpgrade,
          label: 'Upgrade now',
          target_plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
          period,
          trial_days_remaining: effectiveDaysLeft,
          ...(userTier !== undefined && { current_plan: userTier }),
          is_90_day_trial_user: isFakeClinicianTrial,
          is_90_day_trial_ended: isNinetyDayTrialEnded,
        },
      });
      void handleUpgrade(
        period,
        SubscriptionTier.CLINICIAN_PRO_PLUS,
        catalogueVersion === 'v3'
          ? (getCommerceBillingUiPorts().products.getPlanFromPeriod(
              period,
              practiceProduct
            )?.id ?? null)
          : undefined
      );
    },
    [
      handleUpgrade,
      catalogueVersion,
      practiceProduct,
      effectiveDaysLeft,
      userTier,
      isFakeClinicianTrial,
      isNinetyDayTrialEnded,
    ]
  );

  const handleTalkToUsClick = useCallback(() => {
    getCommerceBillingUiPorts().track({
      event_name: getCommerceBillingUiPorts().trackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.UpgradeToPracticeModal,
        action: ButtonAction.PracticeUpgradeModalTalkToUs,
        label: t('pricingPlansModal.talkToUs'),
        target_plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
        trial_days_remaining: effectiveDaysLeft,
        ...(userTier !== undefined && { current_plan: userTier }),
        is_90_day_trial_user: isFakeClinicianTrial,
        is_90_day_trial_ended: isNinetyDayTrialEnded,
      },
    });
    if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
    if (getCommerceBillingUiPorts().isTauri()) {
      void getCommerceBillingUiPorts().openUrlInTauri(
        getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL
      );
    } else {
      window.open(
        getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [t, effectiveDaysLeft, userTier, isFakeClinicianTrial, isNinetyDayTrialEnded]);

  const showExpiredTitle =
    clinicianTrialEnded || newProductTier !== SubscriptionTier.CLINICIAN_PRO_PLUS;
  const effectiveShowExpiredTitle = isPreview ? false : showExpiredTitle;
  const effectiveIsTrialUser = isPreview || isClinicianPracticeTrialUser;
  const currentPlanName = getCommerceBillingUiPorts().getCurrentPlanNameForTrial(
    isInReverseTrial,
    previousStatusBeforeClinicianTrial
  );
  const {
    titleText: trialTitleText,
    featuresTitle: trialFeaturesTitle,
    isShortTrial,
  } = getCommerceBillingUiPorts().useUpgradeModalTrialText({
    trialDaysLeft: effectiveDaysLeft,
    showExpiredTitle: effectiveShowExpiredTitle,
    titleWhenExpiredKey: 'practiceTrial.upgradeModal.titleLevelUpCare',
    plan: 'Practice',
    everythingInPlanPlusKey: 'upgrade.everythingInCurrentPlanPlus' as const,
    currentPlanName: isPreview
      ? getCommerceBillingUiPorts().getCurrentPlanNameForTrial(true, null)
      : currentPlanName,
  });

  const titleText = useMemo(() => {
    if (effectiveIsTrialUser) {
      return trialTitleText;
    } else if (userTier === SubscriptionTier.CLINICIAN_PRO) {
      return t('practiceTrial.upgradeModal.titleTeamAligned');
    } else if (userTier === SubscriptionTier.PRO_PLUS) {
      return t('practiceTrial.upgradeModal.titleClinicianCoPilot');
    } else {
      return trialTitleText;
    }
  }, [effectiveIsTrialUser, userTier, trialTitleText, t]);

  const subtitleText = useMemo(() => {
    if (effectiveIsTrialUser) {
      return t('upgrade.subtitlePractice');
    } else if (userTier === SubscriptionTier.CLINICIAN_PRO) {
      return t('upgrade.subtitlePracticeForClinician');
    } else {
      return t('upgrade.subtitlePracticeForNonTrial');
    }
  }, [effectiveIsTrialUser, userTier, t]);

  const featuresTitle = useMemo(() => {
    if (effectiveIsTrialUser) {
      return trialFeaturesTitle;
    } else if (userTier === SubscriptionTier.FREE) {
      return t('upgrade.everythingInFree');
    } else if (userTier === SubscriptionTier.EVIDENCE) {
      return t('upgrade.everythingInEvidencePlus');
    } else if (userTier === SubscriptionTier.PRO) {
      return t('upgrade.everythingInScribePlus');
    } else if (userTier === SubscriptionTier.CLINICIAN_PRO) {
      return t('upgrade.everythingInClinician');
    } else if (userTier === SubscriptionTier.PRO_PLUS) {
      return t('upgrade.everythingInScribeTeam');
    }
  }, [effectiveIsTrialUser, userTier, trialFeaturesTitle, t]);

  const gamifyTrialExtensionQueryEnabled =
    !isUpgradeDisabledForTeamMember &&
    !shouldDisableButtonForNonStripeManagedTeam &&
    isInReverseTrial &&
    isClinicianPracticeTrialUser &&
    !clinicianTrialEnded;
  const { data: gamifyTrialExtension } =
    getCommerceBillingUiPorts().useQueryGamifyTrialExtension(
      gamifyTrialExtensionQueryEnabled
    );
  // GRO-3284: suppress (Arm B) / delay until the sidebar is done (Arm C).
  const isGamifyArmAllowed = getCommerceBillingUiPorts().useIsGamifyTrialArmAllowed();

  const showExtendTrialButton =
    !isUpgradeDisabledForTeamMember &&
    !shouldDisableButtonForNonStripeManagedTeam &&
    isInReverseTrial &&
    isClinicianPracticeTrialUser &&
    !clinicianTrialEnded &&
    !gamifyTrialExtension?.trial_extended &&
    isGamifyArmAllowed;

  const features = wasPreviouslyPracticeOld
    ? practiceNewFeatures
    : isShortTrial
      ? practiceShortFeatures
      : practiceFeatures;

  const upgradeDisabledTooltip = shouldDisableButtonForNonStripeManagedTeam
    ? t('upgrade.contactSalesToUpgrade')
    : t('upgrade.contactAdminToUpgrade');

  const footerText = useMemo(
    () =>
      !effectiveShowExpiredTitle
        ? t('upgrade.trialEndFooter')
        : t('upgrade.cancelAnytime'),
    [effectiveShowExpiredTitle, t]
  );

  const planChangesArticleUrl = useMemo(
    () => getPlanChangesHelpArticleUrl(intl.locale),
    [intl.locale]
  );

  return {
    isOpen,
    handleOpenChange,
    handleClose,
    handleUpgradeClick,
    handleTalkToUsClick,
    handleExtendTrial,
    handleViewAllPlans,
    isUpgradeDisabled: showNonAdminRequestBanner
      ? false
      : isUpgradeDisabledForTeamMember ||
        (shouldDisableButtonForNonStripeManagedTeam && !showContactSalesInsteadOfUpgrade),
    upgradeDisabledTooltip,
    titleText,
    subtitleText,
    effectiveDaysLeft,
    showNinetyDayTrialVariant,
    showNonAdminRequestBanner,
    featuresTitle,
    features,
    practiceProduct,
    isPending,
    showExtendTrialButton,
    footerText,
    showContactSalesInsteadOfUpgrade,
    handleContactSalesClick,
    hasCallbackRequested,
    practicePlanDisplayName,
    isFromTrialRecap,
    isNinetyDayTrialEnded,
    planChangesArticleUrl,
  };
}
